/**
 * cardgame-draft — the Werte-Kartenspiel's LOCAL, per-user draft (fix: a session
 * expiry / reload mid-game must never lose a run).
 *
 * The surface writes its whole in-progress snapshot here on EVERY mutation, so the
 * work survives a page reload AND the full OIDC re-auth round-trip (both of which
 * blow away in-memory state). On mount the surface restores whichever of {local
 * draft, server snapshot} is newer, and clears the draft on a successful commit.
 *
 * The storage plumbing lives in `local-draft.ts` (shared with the Werte-Sektion's
 * review draft); this module owns only the ONE thing that is cardgame-specific — the
 * structural guard for the pure reducer's `ValueGameSnapshot`. It stays ArkType-free
 * on purpose: it rides in the browser bundle, which the cardgame keeps free of
 * domain-compile side effects (same discipline as the `value-game` /
 * `value-card-catalog` deep imports), so a hand-rolled guard is proportionate.
 */

import type { ValueGameSnapshot } from "@career/definition-v2"
import { createLocalDraftStore, type LocalDraftStore } from "./local-draft"

/** The snapshot phases, mirrored as a runtime set for the structural guard. */
const PHASES = new Set(["reduce", "refine", "lebenswert", "superkraefte", "confirm"])

const isStringArray = (v: unknown): v is string[] => Array.isArray(v) && v.every((x) => typeof x === "string")

/**
 * A total, dependency-free guard for a persisted snapshot. Rejects anything the pure
 * reducer could choke on (missing pile, wrong type, unknown phase). Deliberately
 * structural — the reducer is the authority on game legality; this only ensures the
 * shape is sound enough to hand back to it.
 */
export function isValueGameSnapshot(v: unknown): v is ValueGameSnapshot {
  if (typeof v !== "object" || v === null) return false
  const s = v as Record<string, unknown>
  return (
    typeof s["seed"] === "string" &&
    typeof s["phase"] === "string" &&
    PHASES.has(s["phase"]) &&
    isStringArray(s["order"]) &&
    isStringArray(s["keep"]) &&
    isStringArray(s["out"]) &&
    isStringArray(s["maybe"]) &&
    isStringArray(s["roundWinners"]) &&
    typeof s["pairIndex"] === "number" &&
    typeof s["round"] === "number" &&
    typeof s["rescueBudget"] === "number" &&
    (s["lebenswert"] === null || typeof s["lebenswert"] === "string") &&
    isStringArray(s["superkraefte"]) &&
    typeof s["updatedAt"] === "string"
  )
}

/** The persisted draft: the in-progress snapshot (its `updatedAt` is the freshness clock). */
export type CardgameDraftStore = LocalDraftStore<ValueGameSnapshot>

const KEY_PREFIX = "career:cardgame:draft:v1:"

/** A per-user draft store over localStorage, guarded by {@link isValueGameSnapshot}. */
export function createCardgameDraftStore(userId: string | null, explicitStorage?: Storage): CardgameDraftStore {
  return createLocalDraftStore(KEY_PREFIX, userId, isValueGameSnapshot, explicitStorage)
}
